// =============================================================================
// COMPETING PIXELS BANNER - double-counting warning
// =============================================================================
// The theme app embed reports which OTHER conversion pixels run on the
// storefront. When one of them sends the same purchase the browser way while we
// send it server-side, the channel counts it twice and the merchant sees roughly
// double their real conversions. The advice splits in two, which is the whole
// reason this banner exists:
//   - merchant-controlled source (theme code, GTM): dedup IS possible, they
//     stamp our event id on the browser event. For a purchase that id is the
//     ORDER ID.
//   - Shopify's native Facebook & Instagram channel: it picks its own event ids
//     server-side and exposes them nowhere, so dedup is impossible and one of
//     the two sources has to go.
// Copy is translated in all seven UI languages (i18n/translations.ts,
// `competingPixels`). Strings that wrap a fragment in <code> or <strong> carry a
// {code} / {strong} marker and are split around it, which is the same pattern
// the billing strings use: it keeps one translatable sentence per language
// instead of fragments a translator cannot reorder.
// =============================================================================

import { useNavigate } from 'react-router-dom';
import { Banner, BlockStack, List, Text } from '@shopify/polaris';
import { useOnboardingStatus, type CompetingSource } from '../hooks/api';
import { useTranslation } from '../i18n/LanguageContext';

/** Split a translated sentence around its {token} placeholder. */
function around(sentence: string, token: string): [string, string] {
  const i = sentence.indexOf(token);
  return i < 0 ? [sentence, ''] : [sentence.slice(0, i), sentence.slice(i + token.length)];
}

function labelList(sources: CompetingSource[]): string {
  return sources.map((s) => s.label).join(', ');
}

/** Dating the detection is what lets a merchant confirm a fix took effect. */
function lastDetected(sources: CompetingSource[]): string {
  const newest = sources.reduce(
    (max, s) => Math.max(max, Date.parse(s.lastSeenAt) || 0),
    0,
  );
  return newest > 0 ? new Date(newest).toLocaleString() : '';
}

export function CompetingPixelsBanner() {
  const navigate = useNavigate();
  const { data } = useOnboardingStatus();
  const t = useTranslation();

  const competing = data?.competingPixels;
  if (!competing || competing.sources.length === 0) {
    return null;
  }

  const mustDisable = competing.sources.filter((s) => s.dedup === 'NONE');
  const dedupable = competing.sources.filter((s) => s.dedup === 'EVENT_ID');
  const selfDeduping = competing.sources.filter((s) => s.dedup === 'ORDER_ID');

  // A Google tag on its own is not a problem worth a banner: Google Ads
  // deduplicates on the order id by itself. Staying silent keeps the banner
  // meaningful when it does show up.
  if (mustDisable.length === 0 && dedupable.length === 0) {
    return null;
  }

  const c = t.competingPixels;
  const [dedupPre, dedupPost] = around(c.warningDedup, '{strong}');
  const [metaPre, metaPost] = around(c.itemMeta, '{code}');
  const [othersPre, othersPost] = around(c.itemOthers, '{code}');

  return (
    <BlockStack gap="300">
      {mustDisable.length > 0 && (
        <Banner
          tone="critical"
          title={c.criticalTitle}
          action={{ content: c.criticalAction, onAction: () => navigate('/meta') }}
        >
          <BlockStack gap="200">
            <Text as="p">{c.criticalDetected.replace('{sources}', labelList(mustDisable))}</Text>
            <Text as="p">{c.criticalAdvice}</Text>
            <Text as="p" tone="subdued" variant="bodySm">
              {c.lastDetected.replace('{date}', lastDetected(mustDisable))}
            </Text>
          </BlockStack>
        </Banner>
      )}

      {dedupable.length > 0 && (
        <Banner tone="warning" title={c.warningTitle}>
          <BlockStack gap="200">
            <Text as="p">{c.warningDetected.replace('{sources}', labelList(dedupable))}</Text>
            <Text as="p">
              {dedupPre}
              <strong>{c.warningDedupStrong}</strong>
              {dedupPost}
            </Text>
            <List type="bullet">
              <List.Item>
                {metaPre}
                <code>eventID</code>
                {metaPost}
              </List.Item>
              <List.Item>
                {othersPre}
                <code>event_id</code>
                {othersPost}
              </List.Item>
              <List.Item>{c.itemGtm}</List.Item>
            </List>
            {selfDeduping.length > 0 && (
              <Text as="p" tone="subdued" variant="bodySm">
                {c.selfDeduping.replace('{sources}', labelList(selfDeduping))}
              </Text>
            )}
            <Text as="p" tone="subdued" variant="bodySm">
              {c.lastDetected.replace('{date}', lastDetected(dedupable))}
            </Text>
          </BlockStack>
        </Banner>
      )}
    </BlockStack>
  );
}
